import type { Growth, Landed, MetricKey, Verdict } from "../../results/types";

export type Tone = "neutral" | "success" | "warning";

const METRIC_LABELS: Record<MetricKey, string> = { bookings: "Bookings", serviceSales: "Service sales", newClients: "New clients" };
export const metricLabel = (k: MetricKey): string => METRIC_LABELS[k];

/** Percent change of `a` against `b`, unrounded; null when there is nothing to compare against. */
export function pctChange(a: number, b: number | null): number | null {
  if (b === null || b === 0) return null;
  return ((a - b) / b) * 100;
}

/** Within two points either way counts as flat. */
export function deltaTone(pct: number | null): "success" | "neutral" | "warning" {
  if (pct === null) return "neutral";
  const r = Math.round(pct); // the label shows the rounded value, so the colour follows it
  if (Math.abs(r) <= 2) return "neutral";
  return r > 0 ? "success" : "warning";
}

const whole = (n: number) => Math.round(n);
const aheadBehind = (pct: number) => `${Math.abs(whole(pct))}% ${whole(pct) < 0 ? "behind" : "ahead of"}`;
export const COACH = "Meet with your franchise growth coach to look at your BTB settings.";
const BEFORE = "the 4 weeks before automatic BTB";

function steadyText(g: Growth): string {
  const head = "Bookings are holding steady since BTB went live: ";
  if (g.mode === "before_only") return `${head}${Math.abs(whole(g.sincePct))}% ${whole(g.sincePct) < 0 ? "below" : "above"} ${BEFORE}.`;
  const lastYear = `${whole(g.sincePct) < 0 ? "down" : "up"} ${Math.abs(whole(g.sincePct))}% on the same weeks last year`;
  const trend = g.sincePct - g.beforePct >= 2 ? "better than your trend in" : "in line with how you were trending in";
  return `${head}${lastYear}, ${trend} ${BEFORE}.`;
}

export function verdictBanner(v: Verdict, _goLive: string): { tone: Tone; text: string } {
  switch (v.state) {
    case "too_early": return { tone: "neutral", text: `BTB has been live for ${v.daysLive} ${v.daysLive === 1 ? "day" : "days"}. We'll show a comparison once there are two full weeks.` };
    case "no_history": return { tone: "neutral", text: "This location doesn't have enough history to compare yet." };
    case "steady": return { tone: "success", text: v.bookings ? steadyText(v.bookings) : "Your numbers are holding steady since BTB went live." };
    case "down": {
      const m = metricLabel(v.metric), g = v.growth;
      const head = `${m} are down since BTB went live`;
      if (g.mode === "before_only") return { tone: "warning", text: `${head}: ${Math.abs(whole(g.sincePct))}% below ${BEFORE}. ${COACH}` };
      return { tone: "warning", text: `${head}. In ${BEFORE}, you were ${aheadBehind(g.beforePct)} last year; since BTB you're ${aheadBehind(g.sincePct)}. ${COACH}` };
    }
  }
}

const share = (n: number, l: Landed) => (l.matched === 0 ? 0 : (n / l.matched) * 100);
const PARTS = [
  { key: "first", where: "in the first hour" },
  { key: "middle", where: "mid-shift" },
  { key: "last", where: "in the last hour" },
] as const;

/** Chart values: share of matched appointments per part of the shift, in whole percent. */
export function landedShares(l: Landed): [number, number, number] {
  return [whole(share(l.first, l)), whole(share(l.middle, l)), whole(share(l.last, l))];
}

/** One sentence about the part of the shift whose share moved most. */
export function landedCaption(before: Landed, since: Landed): string {
  if (before.matched === 0 || since.matched === 0) return "There aren't enough appointments yet to compare.";
  const moves = PARTS.map((p) => ({ ...p, from: share(before[p.key], before), to: share(since[p.key], since) }));
  const top = moves.reduce((a, b) => (Math.abs(b.to - b.from) > Math.abs(a.to - a.from) ? b : a));
  if (Math.abs(whole(top.to) - whole(top.from)) < 2) return "Your appointments are landing in the same parts of the shift as before automatic BTB.";
  const up = top.to > top.from;
  return `${up ? "More" : "Fewer"} of your appointments are landing ${top.where}: ${whole(top.to)}%, ${up ? "up" : "down"} from ${whole(top.from)}%.`;
}

/** Signed whole percent, "+7%" / "−2%" (a real minus sign reads better beside digits). */
export function signedPct(pct: number): string {
  const r = whole(pct);
  return `${r < 0 ? "−" : "+"}${Math.abs(r)}%`;
}

/** The banner up to its first full stop, for the all-locations table. */
export function firstSentence(text: string): string {
  const i = text.indexOf(". ");
  return i === -1 ? text : text.slice(0, i + 1);
}

/** A window's total as a per-week rate; null for an empty window. */
export function perWeek(total: number, days: number): number | null {
  return days > 0 ? (total / days) * 7 : null;
}

/** Counts in whole numbers, sales in dollars ("$14.2k" from a thousand up). */
export function formatMetric(k: MetricKey, v: number): string {
  if (k !== "serviceSales") return Math.round(v).toLocaleString();
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v).toLocaleString()}`;
}

export const formatHours = (minutes: number): string => `${(minutes / 60).toFixed(1)} h`;

export const BLOCKED_RANGES = [
  { key: "d30", label: "30 d", caption: "last 30 days" },
  { key: "d60", label: "60 d", caption: "last 60 days" },
  { key: "d90", label: "90 d", caption: "last 90 days" },
  { key: "lifetime", label: "Lifetime", caption: "since BTB went live" },
] as const;
export type BlockedRange = (typeof BLOCKED_RANGES)[number]["key"];
